/* eslint-disable no-unused-vars */
// ════════════════════════════════════════════════════════════════
// useScreenVoice.js
// Custom React hook for voice/tracking boilerplate on GSA screens.
// Replaces ~50 lines of repeated setup on 15+ screens.
// ════════════════════════════════════════════════════════════════

import { useEffect, useRef, useState } from 'react';
import { useAssessment } from "../self-assesment/AssessmentContext";
import { generatePayloadForScreen } from './generatePayloadForScreen';

// These are global singletons in production — imported or accessed via window.
// Adjust import paths to match the production codebase.
import VoiceService from '../voice-agent/VH-Myra-FrontEnd';
import MyraInteraction from '../self-assesment/MyraInteractionController';

export function useScreenVoice({
  screenName,
  trackerName,
  flowName = 'flow_gsa',
  payloadKey,
  commands = {},
  exitReason,
  skip = false,
  skipFlow = false,
  onReady,
}) {
  const { assessmentData } = useAssessment();
  const [isVoiceReady, setIsVoiceReady] = useState(false);
  const commandsRef = useRef(commands);
  const exitReasonRef = useRef(exitReason);
  const intervalRef = useRef(null);
  const originalOnConnectedRef = useRef(null);
  const hasSentPayloadRef = useRef(false);
  const resolvedTracker = trackerName || screenName;

  // Keep commands ref current so handlers aren't stale
  // When skipFlow is true, the screen manages commands manually — don't overwrite.
  useEffect(() => {
    commandsRef.current = commands;
    if (typeof window !== 'undefined' && !skipFlow) {
      // Wrap every command handler with exitHandlerOpen guard
      const guardedCommands = {};
      for (const [name, handler] of Object.entries(commands)) {
        guardedCommands[name] = (...args) => {
          if (window.exitHandlerOpen) return;
          handler(...args);
        };
      }
      window.currentCommandsAudio = guardedCommands;
    }
  }, [commands, skipFlow]);

  // Keep exitReason ref current so cleanup reads the latest value
  useEffect(() => {
    exitReasonRef.current = exitReason;
  }, [exitReason]);

  useEffect(() => {
    if (skip) return;

    // ── Step 1: MyraInteraction.onScreenEnter ──
    if (typeof window !== 'undefined' && MyraInteraction) {
      MyraInteraction.onScreenEnter(screenName);
    }

    // ── Step 2: screenTracker.enterScreen ──
    if (typeof window !== 'undefined' && window.screenTracker) {
      window.screenTracker.enterScreen(resolvedTracker);
    }

    // ── Step 3: initFromUserGesture ──
    if (typeof window !== 'undefined' && window.initFromUserGesture) {
      window.initFromUserGesture();
    }

    // ── Step 5: Show Myra FAB ──
    if (typeof window !== 'undefined' && window.mayraCode) {
      // window.mayraCode.setIsVisible(true);
    }

    // ── skipFlow: boilerplate done, skip flow start / payload / polling ──
    // Screens using skipFlow manage their own commands and flows.
    if (skipFlow) {
      return () => {
        if (typeof window !== 'undefined' && window.screenTracker) {
          window.screenTracker.exitScreen(resolvedTracker, { exitReason: exitReasonRef.current });
        }
      };
    }

    // ── Step 4: Register commands (standard flow only) ──
    if (typeof window !== 'undefined') {
      const guardedCommands = {};
      for (const [name, handler] of Object.entries(commandsRef.current)) {
        guardedCommands[name] = (...args) => {
          if (window.exitHandlerOpen) return;
          handler(...args);
        };
      }
      window.currentCommandsAudio = guardedCommands;
    }

    // ── Step 6–7: Set flow on VoiceService and window ──
    if (typeof window !== 'undefined') {
      if (VoiceService) {
        VoiceService._currentFlow = flowName;
      }
      window.currentFlow = flowName;
    }

    // ── Voice Initialization ──
    function initializeVoice() {
      if (hasSentPayloadRef.current) return;
      if (
        typeof window !== 'undefined' &&
        window.mayraCode &&
        VoiceService?._ws?.readyState === WebSocket.OPEN
      ) {
        const payload = generatePayloadForScreen(payloadKey, assessmentData);
        VoiceService.sendMidJourneyData(payload);
        hasSentPayloadRef.current = true;
        setIsVoiceReady(true);
        if (onReady) onReady();
      }
    }

    // Try immediately
    initializeVoice();

    // Poll every 1s for WebSocket readiness
    intervalRef.current = setInterval(() => {
      initializeVoice();
      if (hasSentPayloadRef.current && intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }, 1000);

    // Backup: override onConnected callback for reconnection
    if (typeof window !== 'undefined' && VoiceService?._callbacks) {
      originalOnConnectedRef.current = VoiceService._callbacks.onConnected;
      VoiceService._callbacks.onConnected = () => {
        if (originalOnConnectedRef.current) {
          originalOnConnectedRef.current();
        }
        initializeVoice();
      };
    }

    // ── Cleanup ──
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }

      // Restore original onConnected
      if (
        typeof window !== 'undefined' &&
        VoiceService?._callbacks &&
        originalOnConnectedRef.current
      ) {
        VoiceService._callbacks.onConnected = originalOnConnectedRef.current;
      }

      // Exit screen tracking — read from ref for latest value
      if (typeof window !== 'undefined' && window.screenTracker) {
        window.screenTracker.exitScreen(resolvedTracker, { exitReason: exitReasonRef.current });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screenName, payloadKey, flowName, skip, skipFlow]);

  return { isVoiceReady };
}
